import { ArrowRight, X } from 'lucide-react';
import { useEffect, useId, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { withViewTransition } from '@/lib/video-files';
import { IconButton } from '../primitives';
import { RecordPreview, RecordSetup } from '../recording/RecordSetup';

/** Made by the app's own renderers (scripts/home-thumbnails.gen.ts): a screen with the presenter's bubble. */
const THUMBNAIL = '/home/recording.webp';

/** The callout and the panel it opens into are one element to the view transition, so the card grows into the recorder. */
const MORPH = { viewTransitionName: 'recorder' } as CSSProperties;

/**
 * Recording is making a video, not editing one, so it doesn't sit among the
 * recipes: it has its own place in the hero. Closed, it is a call to action;
 * open, the same spot holds the recorder's setup (whose camera and microphone
 * previews only run while it is open, so the camera light is never on for nothing).
 */
export function RecordCallout({ open, onOpenChange, loading }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void; readonly loading: string | false }) {
  const { t } = useTranslation();
  const titleId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const wasOpen = useRef(open);

  // Focus follows the morph: into the recorder when it opens, back to the call to action when it closes.
  useEffect(() => {
    if (open && !wasOpen.current) panel.current?.focus({ preventScroll: true });
    if (!open && wasOpen.current) trigger.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);

  const toggle = (next: boolean) => withViewTransition(() => onOpenChange(next));

  if (open) {
    const closeOnEscape = (event: KeyboardEvent) => {
      // A native select or the notes field may use Escape themselves first.
      if (event.key === 'Escape' && !event.defaultPrevented && !loading) toggle(false);
    };
    return (
      <section
        ref={panel}
        tabIndex={-1}
        aria-labelledby={titleId}
        onKeyDown={closeOnEscape}
        style={MORPH}
        className="w-full rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line outline-none sm:p-5 lg:max-w-110"
      >
        <div className="mb-4 flex items-start gap-3">
          <RecordPreview fallback={null} />
          <div className="min-w-0 flex-1 pt-1">
            <h2 id={titleId} className="flex items-center gap-2 text-[17px] leading-tight font-semibold tracking-[-0.02em] text-balance text-fg">
              <RecDot />
              {t('home.record.title')}
            </h2>
            <p className="mt-1 text-[13px] leading-snug text-pretty text-fg-muted">{t('home.record.promise')}</p>
          </div>
          <IconButton label={t('home.record.close')} onClick={() => toggle(false)} disabled={loading !== false} className="-mt-1 -mr-1">
            <X size={17} />
          </IconButton>
        </div>
        <RecordSetup loading={loading} />
      </section>
    );
  }

  return (
    <button
      ref={trigger}
      type="button"
      aria-expanded={false}
      onClick={() => toggle(true)}
      style={MORPH}
      className="group flex w-full items-center gap-4 rounded-[22px] bg-panel p-3 text-left ring-1 ring-line transition-[box-shadow,transform] duration-200 ease-out-soft outline-none hover:shadow-float hover:ring-line-strong focus-visible:ring-2 focus-visible:ring-accent active:scale-[0.99] lg:max-w-110"
    >
      <span className="relative hidden shrink-0 overflow-hidden rounded-[14px] ring-1 ring-line min-[400px]:block">
        <img src={THUMBNAIL} alt="" decoding="async" className="aspect-video h-21 object-cover transition-transform duration-300 ease-out-soft group-hover:scale-[1.04]" />
        <span aria-hidden className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded-full bg-black/65 px-1.5 py-0.5 text-[9px] font-semibold tracking-[0.06em] text-white backdrop-blur-sm">
          <RecDot small />
          REC
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start">
        <span className="text-[16px] leading-snug font-semibold tracking-[-0.02em] text-balance text-fg">{t('home.record.title')}</span>
        <span className="mt-0.5 text-[13px] leading-snug text-pretty text-fg-muted">{t('home.record.tagline')}</span>
        <span className="mt-2.5 inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-accent px-3.5 text-sm font-medium text-accent-ink shadow-[inset_0_1px_0_rgb(255_255_255/0.35)] transition-colors duration-150 group-hover:bg-accent-strong">
          {t('home.record.cta')}
          <ArrowRight size={15} className="transition-transform duration-200 ease-out-soft group-hover:translate-x-0.5" />
        </span>
      </span>
    </button>
  );
}

/** The universal "on air" mark: the one red on the page, and it only breathes when motion is welcome. */
function RecDot({ small }: { readonly small?: boolean }) {
  return (
    <span aria-hidden className={small ? 'relative flex size-1.5' : 'relative flex size-2.5 shrink-0'}>
      <span className="absolute inset-0 rounded-full bg-cut opacity-60 motion-safe:animate-ping" />
      <span className="relative size-full rounded-full bg-cut" />
    </span>
  );
}
